import {
  DANGEROUS_GOODS_CODES,
  EXPIRATION_DATE_DAYS_THRESHOLD,
  ICON_STATUS_MAP,
  MESSAGES_STATUS_THEMES,
  STATUS_MAP,
  STATUS_THEMES,
  SUBMITTED_BADGES
} from "@/common/constants.js";
import {computed, ref} from 'vue'
import Toast from "@/components/atoms/Toast.vue";
import {toast} from "vue3-toastify";

export const getDangerousGoodIcon = (code) => {
  const normalized = code?.trim().toUpperCase()

  if (normalized && DANGEROUS_GOODS_CODES.find(c => c.value === normalized)) {
    return new URL(`../assets/dangerous/${normalized}.png`, import.meta.url).href
  }
}

// Helper to return badge classes for a given status
export function messageBadgeClass(status) {
  const key = `${status}`.toUpperCase()
  const theme = MESSAGES_STATUS_THEMES[key] || MESSAGES_STATUS_THEMES.PENDING
  // Using DaisyUI badge style + theme color
  return [`badge`, `badge-${theme.color}`, `badge-sm`].join(' ')
}

const normalizeStatus = (s) =>
  s?.toString().trim().toLowerCase().replace(/[ -]/g, '_') || ''

export const baseColor = (status) => STATUS_MAP[normalizeStatus(status)]?.[0] ?? 'neutral'

export const statusBadgeClass = (status) => STATUS_MAP[normalizeStatus(status)]?.[2] ?? 'badge badge-xs badge-neutral'
export const statusColorClass = (status) => STATUS_MAP[normalizeStatus(status)]?.[1] ?? 'bg-neutral text-white'

export const getFlightStatusColor = (status) => STATUS_MAP[normalizeStatus(status)]?.[0] || 'primary'
export const getRequestStatusColor = (status) => normalizeStatus(status) === 'accepted' ? 'success' : 'primary'
export const getAircraftStatusColor = (status) => {
  return baseColor(status)
}

export const submittedBadgeClass = (status) => {
  const key = status?.toUpperCase?.()

  return {
    class: 'inline-flex items-center gap-1 py-1 pe-2 rounded-lg bg-base-100 text-sm font-medium text-neutral-800',
    iconClass: SUBMITTED_BADGES[key],
    label: status
  }
}

export const statusIconClass = status => {
  const color = getFlightStatusColor(status)
  return `bi ${ICON_STATUS_MAP[status] || 'bi-dot'} text-${color}`
};

export const commodityIcon = (commodity) => {
  if (commodity === 'general') return 'bi-box'
  else if (commodity === 'special') return 'bi-boxes'
  else return 'bi-snow'
}

export const formatThousands = (
  value,
  currency = '$',
  decimals = 1,
  useAbbreviation = true // 🔹 toggle to control K/M/B suffix
) => {
  if (typeof value !== 'number') return `${currency}0.00`;

  if (useAbbreviation) {
    if (Math.abs(value) >= 1_000_000_000) {
      return `${currency}${(value / 1_000_000_000).toFixed(decimals).replace(/\.0$/, '')}B`;
    }
    if (Math.abs(value) >= 1_000_000) {
      return `${currency}${(value / 1_000_000).toFixed(decimals).replace(/\.0$/, '')}M`;
    }
    if (Math.abs(value) >= 1_000) {
      return `${currency}${(value / 1_000).toFixed(decimals).replace(/\.0$/, '')}K`;
    }
  }

  // 🔹 Default: format as full currency with commas
  return `${currency}${value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`;
};

export function formatMoney(
  value,
  currency = 'USD',                // ISO code, not symbol
  locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US',
  compact = false,                 // auto-use compact notation
  compactThreshold = 1_000,        // start compacting at this absolute value
  currencyDisplay = 'narrowSymbol',// 'symbol' | 'narrowSymbol' | 'code' | 'name'
  signDisplay = 'auto',            // 'auto' | 'never' | 'always' | 'exceptZero'
  minimumFractionDigits,
  maximumFractionDigits,
  accounting = false               // render negatives as (¤1,234.56)
) {
  // 🔹 Handle edge cases
  if (value == null || !Number.isFinite(value)) return '—';

  // 🔹 Normalize and protect the currency code
  try {
    currency = String(currency || 'USD').trim().toUpperCase();
  } catch {
    currency = 'USD';
  }

  const abs = Math.abs(value);
  const useCompact = compact && abs >= compactThreshold;

  // Let Intl infer correct decimals per currency unless overridden
  const baseFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay,
  });
  const currencyDigits =
    minimumFractionDigits ?? baseFormatter.resolvedOptions().minimumFractionDigits;

  // Decide dynamically if decimals are relevant
  const hasDecimals = value % 1 !== 0;
  const minFraction = minimumFractionDigits ?? (hasDecimals ? 1 : 0);
  const maxFraction =
    maximumFractionDigits ??
    (useCompact
      ? Math.min(2, currencyDigits)
      : hasDecimals
        ? currencyDigits
        : 0);

  // Build the formatter
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay,
    notation: useCompact ? 'compact' : 'standard',
    signDisplay,
    minimumFractionDigits: minFraction,
    maximumFractionDigits: maxFraction,
  });

  // Accounting-style negatives
  if (accounting && value < 0) {
    return `(${formatter.format(Math.abs(value))})`;
  }

  return formatter.format(value);
}

export function useSlideTransition() {
  const DIRECTION = {
    FORWARD: 'forward',
    BACKWARD: 'backward',
  }

  const transitionDirection = ref(DIRECTION.FORWARD)

  const transitionName = computed(() =>
    transitionDirection.value === DIRECTION.FORWARD ? 'slide-left' : 'slide-right'
  )

  const setForward = () => (transitionDirection.value = DIRECTION.FORWARD)
  const setBackward = () => (transitionDirection.value = DIRECTION.BACKWARD)

  return {
    transitionDirection,
    transitionName,
    setForward,
    setBackward,
    DIRECTION, // optional
  }
}


export function useStepFormButtons({
                                     isFirstStep,
                                     isLastStep,
                                     isDirty = computed(() => true), // optional
                                     canContinue = computed(() => true), // optional
                                     onBack,
                                     onDraft,
                                     onSubmit,
                                     onNext,
                                   }) {
  const formButtons = computed(() => [
    {
      label: 'Back',
      class: 'btn btn-default px-10',
      action: onBack,
      disabled: isFirstStep.value,
    },
    {
      label: 'Save as Draft',
      class: 'btn btn-default px-10',
      action: onDraft,
      disabled: isDirty ? !isDirty.value : false,
    },
    {
      label: isLastStep.value ? 'Submit' : 'Continue',
      class: 'btn btn-accent px-10 text-white',
      action: isLastStep.value ? onSubmit : onNext,
      disabled: canContinue ? !canContinue.value : false,
    },
  ])

  return {formButtons}
}

// utils/plain.js  (or inside the store file, above defineStore)
export function toPlainSafe(value) {
  return JSON.parse(JSON.stringify(
    value,
    (k, v) => {
      // Files can't go to structuredClone/localStorage; store metadata only
      if (typeof File !== 'undefined' && v instanceof File) {
        return {name: v.name, size: v.size, type: v.type, lastModified: v.lastModified}
      }
      // Dates → ISO
      if (v instanceof Date) return v.toISOString()
      // Vue refs (defensive)
      if (v && typeof v === 'object' && v.__v_isRef) return v.value
      return v
    }
  ))
}

export const showInfo = (title, message = "") => {
  toast(
    Toast,
    {
      type: "info",
      contentProps: {
        title: title,
        message: message,
        date: new Date().toLocaleTimeString(),
      }
    }
  )
}

export const showSuccess = (title, message = "") => {
  toast(
    Toast,
    {
      type: "success",
      contentProps: {
        title: title,
        message: message,
        date: new Date().toLocaleTimeString(),
      }
    }
  )
}

export const showWarning = (title, message = "") => {
  toast(
    Toast,
    {
      type: "warning",
      contentProps: {
        title: title,
        message: message,
        date: new Date().toLocaleTimeString(),
      }
    }
  )
}

export const showError = (title, message = "") => {
  toast(
    Toast,
    {
      type: "error",
      contentProps: {
        title: title,
        message: message,
        date: new Date().toLocaleTimeString(),
      }
    }
  )
}

export const showNotification = (notification) => {
  const {severity, title, body} = notification ?? {}
  if (severity === 'Success') return showSuccess(title, body)
  if (severity === 'Warning') return showWarning(title, body)
  if (severity === 'Critical') return showError(title, body)
  return showInfo(title, body)
}

export function capitalize(s) {
  if (typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const getSegmentLabel = (segment) => {
  if (segment.city && segment.iata_code) {
    return `${segment.city} (${segment.iata_code})`
  } else if (segment.city) {
    return segment.city
  } else if (segment.iata_code) {
    return segment.iata_code
  } else {
    return null
  }
}

export const getCompactSegmentLabel = (
  segment,
  namesFirst = false,
  cityFirst = false
) => {
  if (!segment) return null

  const airportCode = segment.code?.split("-")?.[0] || null
  const cityCode = segment.code?.split("-")?.[1] || null
  const countryCode = segment.code || null

  const cityName = segment.city?.name || segment.city || null

  if (cityFirst && cityName) {
    return cityName
  }

  // 🔹 NAMES FIRST
  if (namesFirst) {
    switch (segment.kind) {
      case 'airport': return segment.name || airportCode || null
      case 'country': return segment.name || countryCode || null
      case 'city':    return segment.name || cityCode || null
      case 'region':  return segment.name || countryCode || null
    }
    return segment.name || segment.iata_code || null
  }

  // 🔹 DEFAULT (compact mode)
  switch (segment.kind) {
    case 'airport': return airportCode || segment.name || null
    case 'country': return countryCode || segment.name || null
    case 'city':    return cityCode || segment.name || null
    case 'region':  return segment.name || countryCode || null
  }

  return segment.iata_code || null
}

export const getItemMessageTheme = (status) => {
  return STATUS_THEMES[status] ?? STATUS_THEMES.draft
}

export function autoFormatWeight(value, unit = "kg", locale = navigator.language) {
  if (value == null || isNaN(value)) return "—";

  // 1️⃣ Normalize to kilograms
  let kg;
  switch (unit) {
    case "g":
      kg = value / 1000;
      break;
    case "T":
      kg = value * 1000;
      break;
    case "lbs":
      kg = value / 2.20462;
      break;
    default:
      kg = value;
      break;
  }

  // 2️⃣ Choose the best display unit
  let displayValue, displayUnit;
  if (kg < 1) {
    displayValue = kg * 1000;
    displayUnit = "g";
  } else if (kg >= 1000) {
    displayValue = kg / 1000;
    displayUnit = "T";
  } else {
    displayValue = kg;
    displayUnit = "kg";
  }

  // 3️⃣ Locale-aware number formatting
  const formatter = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: displayValue < 10 ? 2 : 0,
  });

  return `${formatter.format(displayValue)} ${displayUnit}`;
}

const present = v => v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === '')

export const isValidRequest = (req) => {
  const f = req?.flights?.[0] // choosing the first flight for validation
  if (!f) return false
  return [f.preferredOrigin, f.preferredDestination, f.departureDate, f.arrivalDate].every(present)
}

export const getExpirationIcon = (expState) => {
  switch (expState) {
    case 'plenty':
      return {icon: 'bi-calendar3'}
    case 'few':
      return {icon: 'bi-exclamation-circle'}
    case 'past':
      return {icon: 'bi-calendar-minus'}
  }
}

export const getExpirationState = (daysLeft) => {
  if (daysLeft <= 0) return 'past'
  if (daysLeft <= EXPIRATION_DATE_DAYS_THRESHOLD) return 'few'
  return 'plenty'
}

export const getExpirationColor = (expState) => {
  if (expState === 'past') return 'bg-error/10 text-error'
  if (expState === 'few') return 'bg-warning/10 text-warning'
  return 'bg-accent/10 text-accent'
}

export const getExpirationDateTooltipText = (expState, daysLeft) => {
  if (expState === 'past') {
    const daysAgo = Math.abs(daysLeft)
    return daysAgo === 0
      ? 'Expired today.'
      : `Expired ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago.`
  }
  return `You have ${daysLeft} day${daysLeft === 1 ? '' : 's'} left.`
}

export function validateRequiredField(data, field, fnOrReverse = false) {
  const pathParts = field.split('.')
  if (pathParts.length > 1) {
    const values = getNestedValues(data, pathParts)
    // inválido si: no hay valores, o alguno de los valores es inválido
    return !(values.length > 0 && values.every(val => !isInvalid(val, fnOrReverse)))
  } else {
    const value = data[field]
    return isInvalid(value, fnOrReverse)
  }
}

export function isInvalid(value, fnOrReverse = false) {
  if (typeof fnOrReverse === 'function') {
    return fnOrReverse(value) // la función debe devolver true cuando el valor sea inválido
  }
  const reverse = !!fnOrReverse
  const basic = (Array.isArray(value) && value.length === 0) || (!Array.isArray(value) && !value)
  return reverse ? !basic : basic
}

export function getNestedValues(obj, pathParts) {
  if (!obj) return []
  const [key, ...rest] = pathParts
  const current = obj[key]
  if (rest.length === 0) {
    if (Array.isArray(current)) return current
    return [current]
  }
  if (Array.isArray(current)) {
    return current.flatMap(item => getNestedValues(item, rest))
  }
  return getNestedValues(current, rest)
}

export function isNUmberGreaterThanZero(value) {
  const toNum = x => {
    const n = Number(x);
    return Number.isFinite(n) ? n : NaN
  }
  return Number.isFinite(toNum(value)) && toNum(value) > 0
}

export function locationTypeFromCode(code) {
  const map = {
    airport: "✈️",
    city: "📍",
    country: "🌍",
    region: "🌐",
  }

  return map[code] ?? "📍"
}

export const formatLocationWithBadge = (type, name) => {
  console.log("DATA type:", type)
  console.log("DATA name:", name)
  return `<span class="badge badge-xs px-2 text-[10px] text-primary font-normal rounded-sm border-accent/20 mr-1">${type}</span><span class="text-primary">${name}</span>`
}

export const formatLocationWithIconType = (location) => {
  if (!location?.name) return ''
  return ` ${locationTypeFromCode(location.kind)} ${getCompactSegmentLabel(location)}`
}

export function normalizeInference(raw = {}) {
  const sd = raw['Shipment Details'] ?? {}
  const piecesRaw = Array.isArray(sd['Pieces']) ? sd['Pieces'] : []

  const pieces = piecesRaw.map(p => ({
    pieceQuantity: p?.['Piece(quantity)'],
    pieceDescription: p?.['Piece(Description)'],
    pieceStackable: p?.['Piece (Stackable)'],
    pieceTurnable: p?.['Piece(Turnable)'],
    lengthPiece: p?.['Length(Piece)'],
    widthPiece: p?.['Width(Piece)'],
    heightPiece: p?.['Height(Piece)'],
    payloadPiece: p?.['Payload(Piece)'],
    packingPiece: p?.['Packaging'],
    volumePiece: p?.['Volume(Piece)'],
    rawPieces: p?.['RawPieces'],
  }))

  return {
    requestId: raw['RequestID'],
    internalReference: raw['Internal Reference'],
    typeOfFlight: raw['Type of Flight'],
    shipper: raw['Shipper'],
    consignee: raw['Consignee'],
    preferredOrigin: raw['Preferred Origin'],
    alternativeOrigin: raw['Alternative Origin'],
    preferredDestination: raw['Preferred Destination'],
    alternativeDestination: raw['Alternative Destination'],
    dates: raw['Dates'],
    preferredAircraftType: raw['Preferred Aircraft Type'],
    alternativeAircraftType: raw['Alternative Aircraft Type'],
    commodityType: raw['Commodity Type'],
    dangerousGoods: raw['Dangerous Goods'],
    dangerousGoodsCode: raw['Dangerous Goods Code'],
    dangerousGoodsRestricted: raw['Dangerous Goods restricted'],
    dangerousGoodsUnNumber: raw['Dangerous Goods UN number'],
    shcList: raw['SHC List'],
    additionalNotes: raw['Additional Notes'],
    shipmentDetails: {
      product: sd['Product'],
      item: sd['Item'],
      pieces,
    },
    _raw: raw,
  }
}